<template>
  <button
    @click="emit('buttonClicked')"
    @mouseenter="showTooltip"
    @mouseleave="hideTooltip"
    ref="buttonRef"
    type="button"
    :disabled="disabled"
    class="group relative font-medium rounded-md text-sm p-1.5 text-center inline-flex items-center border hover:bg-theme-100 hover:border-theme-300 hover:text-theme-600"
    :class="[
      active ? 'bg-theme-100 border-theme-300 text-theme-600' : 'text-gray-500 border-gray-300',
      disabled ? 'pointer-events-none ' : '',
    ]">
    <slot />
    <Teleport to="body">
      <div
        v-if="tooltipVisible"
        ref="tooltipRef"
        :style="{
          position: strategy,
          top: y ? `${y}px` : '',
          left: x ? `${x}px` : '',
          zIndex: 9999,
        }"
        class="relative flex flex-col items-center px-3 py-2 text-gray-600 bg-white rounded-lg shadow-lg">
        <span class="text-xs truncate">{{ tooltipText }}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="absolute w-4 h-4 text-white fill-current"
          :style="{ bottom: '-8px', left: 'calc(50% - 8px)', transform: 'rotate(45deg)' }"
          viewBox="0 0 24 24">
          <path d="M20 3H4a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1z"></path>
        </svg>
      </div>
    </Teleport>
  </button>
</template>

<script setup lang="ts">
import { ref, watchEffect } from 'vue';
import { useFloating, autoUpdate, offset, flip, shift, hide } from '@floating-ui/vue';

defineProps({
  active: {
    type: Boolean,
    default: false,
    required: false,
  },
  tooltipText: {
    type: String,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
    required: false,
  },
});

const emit = defineEmits(['buttonClicked']);

const buttonRef = ref<HTMLElement | null>(null);
const tooltipRef = ref<HTMLElement | null>(null);
const tooltipVisible = ref(false);

const { x, y, strategy, update, middlewareData } = useFloating(buttonRef, tooltipRef, {
  placement: 'top',
  middleware: [offset(5), flip(), shift(), hide()],
  whileElementsMounted: (referenceEl, floatingEl, update) => {
    return autoUpdate(referenceEl, floatingEl, update);
  },
});

watchEffect(() => {
  if (middlewareData.value.hide?.referenceHidden) {
    tooltipVisible.value = false;
  }
});

function showTooltip() {
  tooltipVisible.value = true;
  update();
}

function hideTooltip() {
  tooltipVisible.value = false;
}
</script>
